import useTranslator from "../hooks/useTranslator";
import { useContext, useState, useEffect } from "react";
import LandLordContext from "../context/landlord/LandLordContext";
import AuthContext from "../context/auth/AuthContext";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "../helper/common";
import { WORKSPACE_ROLES_CAPABILITIES } from "../Utils/workspaceRoleCapabilities";
import {
  Badge,
  Button,
  ChoiceGroup,
  Icon,
  Modal,
  SkeletonList,
  Switch,
} from "./design";
import "./TopUpCreditModal.css";
import BillingDetailsFields from "./BillingDetailsFields";

const EMPTY_BILLING = {
  company_name: "",
  full_name: "",
  tax_id: "",
  country: "",
  city: "",
  address: "",
  postal_code: "",
};

const TopUpCreditModal = ({ open, setOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { _t } = useTranslator();
  const { addCredit, getPricingPlans } = useContext(LandLordContext);
  const { user } = useContext(AuthContext);
  const { workspaceId } = useParams();

  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [autoTopUp, setAutoTopUp] = useState(false);
  const [requestInvoice, setRequestInvoice] = useState(false);
  const [billingType, setBillingType] = useState("business");
  const [billingData, setBillingData] = useState(EMPTY_BILLING);
  const [creditPlans, setCreditPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPricingPlans = async () => {
    try {
      setLoading(true);
      const response = await getPricingPlans();
      const data = response?.data || response;
      if (data) {
        const formattedPlans = data.map((plan) => ({
          id: plan.id || plan._id,
          credits: plan.credits,
          views: plan.views,
          pricePerProfile: plan.pricePerProfile,
          price: plan.price,
          badge: plan.badge ? _t("mostPopular") : null,
          isEnterprise: plan.isEnterprise || plan.type === "enterprise",
        }));
        setCreditPlans(formattedPlans);
        if (formattedPlans.length > 0 && !selectedPlan) {
          const featuredPlan =
            formattedPlans.find((p) => p?.badge) || formattedPlans[0];
          setSelectedPlan(featuredPlan.id);
        }
      }
    } catch (err) {
      console.log(err);
      toast.error("Error while fetching pricing plans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchPricingPlans();
      const saved = user?.current_workspace?.billing_details;
      if (saved) {
        setBillingType(saved.billing_type || "business");
        setBillingData({
          company_name: saved.company_name || "",
          full_name: saved.full_name || "",
          tax_id: saved.tax_id || "",
          country: saved.country || "",
          city: saved.city || "",
          address: saved.address || "",
          postal_code: saved.postal_code || "",
        });
      }
    }
  }, [open]);

  const handleAddCredit = () => {
    setButtonDisabled(true);
    addCredit({
      planId: selectedPlan,
      autoTopUp,
      requestInvoice,
      ...(requestInvoice && {
        billing: {
          billing_type: billingType,
          ...(billingType === "business"
            ? {
                company_name: billingData.company_name,
                tax_id: billingData.tax_id,
              }
            : { full_name: billingData.full_name }),
          country: billingData.country,
          city: billingData.city,
          address: billingData.address,
          postal_code: billingData.postal_code,
        },
      }),
    })
      .then((res) => {
        const data = res.success_res;
        window.location.replace(data?.paymentUrl);
      })
      .catch((err) => {
        console.log(err);
        toast.error("Error while adding credits");
      })
      .finally(() => {
        setButtonDisabled(false);
      });
  };

  const close = () => {
    setSelectedPlan(null);
    setRequestInvoice(false);
    setOpen();
  };

  const handleCreditHistory = () => {
    navigate(`/${workspaceId}/credits-ledger`);
    close();
  };

  const handleContactSales = () => {
    window.open("https://skortorent.com/?chat", "_blank");
  };

  const handleEditBillingDetails = () => {
    const params = new URLSearchParams(location.search);
    params.set("openEditableTab", "billing");
    navigate(
      { pathname: location.pathname, search: `?${params.toString()}` },
      { replace: true }
    );
    close();
  };

  const billingComplete = (() => {
    if (!requestInvoice) return true;
    const {
      company_name,
      full_name,
      tax_id,
      country,
      city,
      address,
      postal_code,
    } = billingData;
    const baseOk =
      [country, city, address].every((v) => v.trim() !== "") &&
      postal_code.trim().length === 5;
    return billingType === "business"
      ? baseOk && company_name.trim() !== "" && tax_id.trim() !== ""
      : baseOk && full_name.trim() !== "";
  })();

  const filteredPlans = autoTopUp
    ? creditPlans.filter((plan) => !plan.isEnterprise)
    : creditPlans;

  const planOptions = [
    ...filteredPlans.map((plan) => ({
      value: String(plan.id),
      label: (
        <>
          {plan.credits} {_t("credits")}
          {plan.badge && <Badge tone="ink">{plan.badge}</Badge>}
        </>
      ),
      description: `x${plan.views} ${_t("profileViews")} · ${plan.pricePerProfile}/${_t("perProfile")}`,
      aside: `€ ${plan.price}`,
    })),
    // not a plan: picking it opens the sales chat
    ...(!autoTopUp
      ? [
          {
            value: "enterprise",
            label: _t("enterprise"),
            description: `${_t("customVolume")} · ${_t("contactSales")}`,
            aside: <Icon name="arrowRight" />,
          },
        ]
      : []),
  ];

  const onPlanChange = (e) => {
    if (e.target.value === "enterprise") {
      handleContactSales();
      return;
    }
    const plan = filteredPlans.find((p) => String(p.id) === e.target.value);
    if (plan) setSelectedPlan(plan.id);
  };

  return (
    <Modal
      open={open}
      onClose={close}
      size="md"
      icon={
        <img
          className="top-up-credit-modal__coin"
          src="/images/credit-point1.svg"
          alt=""
        />
      }
      title={_t("topUpCredits")}
      description={`5 ${_t("creditsWillBeUsedToViewEachTenantProfile")}`}
      actions={
        <>
          <Button variant="secondary" size="lg" onClick={handleCreditHistory}>
            {_t("creditHistory")}
          </Button>
          <Button
            size="lg"
            onClick={handleAddCredit}
            loading={buttonDisabled}
            disabled={loading || !billingComplete}
          >
            {buttonDisabled ? _t("redirecting") : _t("buy")}
          </Button>
        </>
      }
    >
      <div className="top-up-credit-modal">
        {loading ? (
          <SkeletonList rows={4} label={_t("loading")} />
        ) : (
          <>
            <ChoiceGroup
              name="credit_plan"
              layout="list"
              options={planOptions}
              value={String(selectedPlan)}
              onChange={onPlanChange}
            />

            <div className="top-up-credit-modal__settings">
              <Switch
                id="credit-auto-top-up"
                label={_t("autoTopUp")}
                description={_t("autoTopUpDescription")}
                checked={autoTopUp}
                onChange={setAutoTopUp}
              />
              <Switch
                id="credit-request-invoice"
                label={_t("requestInvoice")}
                checked={requestInvoice}
                onChange={setRequestInvoice}
              >
                {requestInvoice && (
                  <div className="top-up-credit-modal__billing">
                    <BillingDetailsFields
                      billingType={billingType}
                      onBillingTypeChange={setBillingType}
                      billingData={billingData}
                      onFieldChange={(key, value) =>
                        setBillingData((d) => ({ ...d, [key]: value }))
                      }
                      headExtra={
                        WORKSPACE_ROLES_CAPABILITIES?.manageBilling?.includes(
                          user?.current_workspace_role
                        ) && (
                          <Button
                            variant="secondary"
                            size="sm"
                            iconStart={<Icon name="edit" size={14} />}
                            onClick={handleEditBillingDetails}
                          >
                            {_t("editBillingDetails")}
                          </Button>
                        )
                      }
                    />
                  </div>
                )}
              </Switch>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default TopUpCreditModal;
